import {
    Stack,
    Group,
    Box,
    ActionIcon,
    TextInput,
    Select,
    Button,
    Divider,
    Text,
    rem,
    useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { UseFormReturnType } from "@mantine/form";
import { Plus, Trash, ArrowUp, ArrowDown } from "lucide-react";

interface TabGroupTabsConfigProps {
    form: UseFormReturnType<any>;
    isSaving: boolean;
    reports: { value: string; label: string }[];
    charts: { value: string; label: string }[];
    dashboards: { value: string; label: string }[];
    forms: { value: string; label: string }[];
}

export function TabGroupTabsConfig({
    form,
    isSaving,
    reports,
    charts,
    dashboards,
    forms,
}: TabGroupTabsConfigProps) {
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

    const handleAddTab = () => {
        form.insertListItem("tabs", {
            title: "",
            type: "report",
            reportId: null,
            chartId: null,
            dashboardId: null,
            formId: null,
        });
    };

    const handleRemoveTab = (index: number) => {
        form.removeListItem("tabs", index);
    };

    const handleMoveTab = (from: number, to: number) => {
        if (to < 0 || to >= form.values.tabs.length) return;
        form.reorderListItem("tabs", { from, to });
    };

    return (
        <Stack gap="md">
            <Divider label="Tab Items" labelPosition="center" />

            <Stack gap="sm">
                {form.values.tabs.map((item: any, index: number) => (
                    <Box
                        key={index}
                        p="md"
                        style={{
                            borderRadius: 'var(--mantine-radius-md)',
                            border: '1px solid var(--modal-border)',
                            backgroundColor: 'var(--mantine-color-body)',
                            position: 'relative'
                        }}
                    >
                        <Group align="flex-start" wrap={isMobile ? "wrap" : "nowrap"}>
                            {/* Reorder actions */}
                            <Stack gap={4} pt={4} hidden={isMobile}>
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    color="gray"
                                    onClick={() => handleMoveTab(index, index - 1)}
                                    disabled={index === 0 || isSaving}
                                >
                                    <ArrowUp size={14} />
                                </ActionIcon>
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    color="gray"
                                    onClick={() => handleMoveTab(index, index + 1)}
                                    disabled={index === form.values.tabs.length - 1 || isSaving}
                                >
                                    <ArrowDown size={14} />
                                </ActionIcon>
                            </Stack>

                            {/* Mobile reorder and delete row */}
                            {isMobile && (
                                <Group justify="space-between" w="100%" mb="xs">
                                    <Group gap="xs">
                                        <ActionIcon
                                            variant="light"
                                            size="sm"
                                            color="gray"
                                            onClick={() => handleMoveTab(index, index - 1)}
                                            disabled={index === 0 || isSaving}
                                        >
                                            <ArrowUp size={14} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="light"
                                            size="sm"
                                            color="gray"
                                            onClick={() => handleMoveTab(index, index + 1)}
                                            disabled={index === form.values.tabs.length - 1 || isSaving}
                                        >
                                            <ArrowDown size={14} />
                                        </ActionIcon>
                                        <Text size="xs" fw={700} c="dimmed">Tab #{index + 1}</Text>
                                    </Group>
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        onClick={() => handleRemoveTab(index)}
                                        disabled={isSaving}
                                    >
                                        <Trash size={16} />
                                    </ActionIcon>
                                </Group>
                            )}

                            <Stack className="flex-1" gap="xs" style={{ width: isMobile ? '100%' : 'auto' }}>
                                <Group grow wrap={isMobile ? "wrap" : "nowrap"} align="flex-end">
                                    <TextInput
                                        label="Tab Title"
                                        placeholder="e.g., Performance Metrics"
                                        required
                                        disabled={isSaving}
                                        style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}
                                        {...form.getInputProps(`tabs.${index}.title`)}
                                    />
                                    <Select
                                        label="Content Type"
                                        data={[
                                            { value: "report", label: "Report" },
                                            { value: "chart", label: "Chart" },
                                            { value: "dashboard", label: "Dashboard" },
                                            { value: "form", label: "Form" },
                                        ]}
                                        required
                                        disabled={isSaving}
                                        style={{ flex: isMobile ? 1 : 0, minWidth: isMobile ? '100%' : '120px' }}
                                        {...form.getInputProps(`tabs.${index}.type`)}
                                        onChange={(val) => {
                                            form.setFieldValue(`tabs.${index}.type`, val as any);
                                            form.setFieldValue(`tabs.${index}.reportId`, null);
                                            form.setFieldValue(`tabs.${index}.chartId`, null);
                                            form.setFieldValue(`tabs.${index}.dashboardId`, null);
                                            form.setFieldValue(`tabs.${index}.formId`, null);
                                        }}
                                    />
                                </Group>

                                {item.type === "report" && (
                                    <Select
                                        label="Select Report"
                                        placeholder="Choose a report"
                                        searchable
                                        data={reports}
                                        required
                                        disabled={isSaving}
                                        {...form.getInputProps(`tabs.${index}.reportId`)}
                                    />
                                )}
                                {item.type === "chart" && (
                                    <Select
                                        label="Select Chart"
                                        placeholder="Choose a chart"
                                        searchable
                                        data={charts}
                                        required
                                        disabled={isSaving}
                                        {...form.getInputProps(`tabs.${index}.chartId`)}
                                    />
                                )}
                                {item.type === "dashboard" && (
                                    <Select
                                        label="Select Dashboard"
                                        placeholder="Choose a dashboard"
                                        searchable
                                        data={dashboards}
                                        required
                                        disabled={isSaving}
                                        {...form.getInputProps(`tabs.${index}.dashboardId`)}
                                    />
                                )}
                                {item.type === "form" && (
                                    <Select
                                        label="Select Form"
                                        placeholder="Choose a form"
                                        searchable
                                        data={forms}
                                        required
                                        disabled={isSaving}
                                        {...form.getInputProps(`tabs.${index}.formId`)}
                                    />
                                )}
                            </Stack>

                            {!isMobile && (
                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    mt={28}
                                    onClick={() => handleRemoveTab(index)}
                                    disabled={isSaving}
                                >
                                    <Trash size={16} />
                                </ActionIcon>
                            )}
                        </Group>
                    </Box>
                ))}

                <Button
                    variant="light"
                    color="gray"
                    leftSection={<Plus size={16} />}
                    onClick={handleAddTab}
                    fullWidth
                    disabled={isSaving}
                    size="md"
                    radius="md"
                    style={{ borderStyle: 'dashed', borderWidth: rem(1) }}
                >
                    Add New Tab
                </Button>
            </Stack>
        </Stack>
    );
}
